import * as React from 'react';
import useNavData from 'cms/hooks/useNavData';
import { useSelector } from 'react-redux';
import { isAdBarOpen } from 'core/redux/adBar/selectors';
import { useLocation } from '@reach/router';
import { graphql, useStaticQuery } from 'gatsby';
import { aggregateGoogleReviews } from 'lib/util';
import { GoogleReviewsLocation } from 'locations/lib/types';
import parse from 'html-react-parser';
import { AdbarObject, Script } from 'lib/types';
import GlobalTrackingPixels from 'analytics/components/GlobalTrackingPixels';
import BypassBlocks from 'a11y/components/BypassBlocks';
import Main, { MainProps } from '../Main';
import AdBar from '../AdBar';
import Footer from '../Footer';
import Nav from '../Nav';
import GlobalEffects from '../GlobalEffects';
import GoogleReviewsBadge from '../GoogleReviewsBadge';
import useCitiesWithNumberOfLocations from '../../hooks/useCitiesWithNumberOfLocations';
import * as styles from './Layout.module.scss';
import UnbounceScripts from '../UnbounceScripts';

type LayoutProps = {
    navClasses?: string;
    footerClasses?: string;
    showFooter?: boolean;
    withFooterTopMargin?: boolean;
    customAdbar?: AdbarObject[];
    mainClasses?: MainProps['classes'];
    fullWidth?: boolean;
    bookingCitySlug?: string;
    bookingLocationSlug?: string;
    isCareerPage?: boolean;
};

const layoutQuery = graphql`
    fragment PageData on Page {
        ...PageMetaData
        pageData
    }
    query LayoutQuery {
        allPage {
            nodes {
                slug
                googleReviewsLocations {
                    name
                    googleReviews {
                        averageRating
                        bestRating
                        reviews {
                            comment
                            rating
                            reviewer {
                                displayName
                                profilePhotoUrl
                            }
                        }
                        totalReviewCount
                        worstRating
                    }
                }
                adbars {
                    startTime
                    endTime
                    title
                }
            }
        }
        unbounceScripts: scripts(namespace: { eq: "unbounce" }) {
            scripts {
                script
                description
            }
        }
    }
`;

type LayoutQuery = {
    allPage: {
        nodes: ReadonlyArray<{
            slug: string;
            googleReviewsLocations: GoogleReviewsLocation[];
            adbars: AdbarObject[];
        }>;
    };
    unbounceScripts: {
        scripts: Script[];
    };
};

type SlugsGoogleReviews = {
    [slug: string]: GoogleReviewsLocation[];
};

type SlugsAdBars = {
    [slug: string]: AdbarObject[];
};

export default function Layout({
    children,
    navClasses,
    mainClasses,
    footerClasses,
    showFooter = true,
    withFooterTopMargin = true,
    customAdbar,
    fullWidth,
    bookingCitySlug,
    bookingLocationSlug,
    isCareerPage = false,
}: React.PropsWithChildren<LayoutProps>) {
    const { nav, footer } = useNavData();
    let isAdBarVisible = useSelector(isAdBarOpen);
    const { pathname } = useLocation();

    const { allPage, unbounceScripts } =
        useStaticQuery<LayoutQuery>(layoutQuery);

    const now = `${new Date().getHours()}:${new Date().getMinutes()}`;

    let adbarTitle;

    const adbars = allPage.nodes.reduce((map: SlugsAdBars, obj) => {
        const copy = { ...map };
        copy[obj.slug] = obj.adbars;
        return copy;
    }, {});

    const googleReviews = allPage.nodes.reduce(
        (map: SlugsGoogleReviews, obj) => {
            const copy = { ...map };
            copy[obj.slug] = obj.googleReviewsLocations;
            return copy;
        },
        {},
    );
    const cities = useCitiesWithNumberOfLocations();
    const navItems = React.useMemo(() => {
        if (cities.length > 0) {
            const locationsMenuItemIdx = nav.items.findIndex(
                (item) => item.label === 'Locations',
            );

            if (locationsMenuItemIdx > -1) {
                const locationsMenuItem = nav.items[locationsMenuItemIdx];
                const newLocationsMenuItem = {
                    ...locationsMenuItem,
                    subItems: [
                        ...cities.map((city, idx) => ({
                            id: city.cityId,
                            to: `/regions/${city.slug}`,
                            external: false,
                            label: city.label,
                            sortOrder: idx,
                        })),
                        ...(locationsMenuItem.subItems ?? []).map(
                            (item, idx) => ({
                                ...item,
                                sortOrder: cities.length + idx,
                            }),
                        ),
                    ],
                };

                return [
                    ...nav.items.slice(0, locationsMenuItemIdx),
                    newLocationsMenuItem,
                    ...nav.items.slice(locationsMenuItemIdx + 1),
                ];
            }
        }

        return nav.items;
    }, [nav.items, cities]);

    let pageReviews: GoogleReviewsLocation[] = [];

    let index = pathname.replace(/^\/|\/$/g, '');

    if (index === '') {
        index = 'home';
    } else if (index.startsWith('blog/')) {
        index = 'blog';
    }

    const isLocationDetailPage = index.startsWith('c/');

    if (googleReviews[index]) {
        pageReviews = googleReviews[index];
    }

    const selectedAdbar = customAdbar ?? adbars[index];

    if (selectedAdbar) {
        selectedAdbar?.forEach((adbar) => {
            const endTime = adbar.endTime === '00:00' ? '24:00' : adbar.endTime;
            if (
                adbar.startTime &&
                adbar.endTime &&
                adbar.startTime <= now &&
                endTime >= now
            ) {
                adbarTitle = adbar.title;
            }
        });
    }

    if (!adbarTitle) {
        isAdBarVisible = false;
    }

    const compoundReviews = aggregateGoogleReviews(pageReviews);

    const showReviews =
        compoundReviews.averageRating !== 0 &&
        compoundReviews.totalReviewCount !== 0;

    return (
        <GlobalEffects>
            <BypassBlocks />
            {isAdBarVisible && (
                <AdBar>
                    <span className={styles.centeredText}>
                        {parse(adbarTitle || '')}
                    </span>
                </AdBar>
            )}
            <Nav
                logoLink="/"
                prefix=""
                items={navItems}
                showCTA={!isLocationDetailPage}
                classes={navClasses}
                bookingCitySlug={bookingCitySlug}
                bookingLocationSlug={bookingLocationSlug}
                isCareerPage={isCareerPage}
            />
            <Main classes={mainClasses} fullWidth={fullWidth}>
                {children}
            </Main>
            {showFooter && (
                <Footer
                    withTopMargin={withFooterTopMargin}
                    className={footerClasses}
                    prefix=""
                    items={footer.items}
                    isCareerPage={isCareerPage}
                />
            )}
            {showReviews && (
                <GoogleReviewsBadge
                    googleReviews={compoundReviews}
                    locationTitle="Bond Vet"
                />
            )}
            <GlobalTrackingPixels />
            {unbounceScripts?.scripts && (
                <UnbounceScripts scripts={unbounceScripts.scripts} />
            )}
        </GlobalEffects>
    );
}
